import { useState, useMemo, useRef, useEffect } from 'react'
import { useTheme } from '../context/ThemeContext'

/**
 * Searchable dropdown for picking the second item (lease or well) in Compare mode.
 * Flattens the combo/well hierarchy into "Lease" and "Lease › Well" options.
 *
 * @param {Array} combos - selector hierarchy from /api/well-analysis
 * @param {string} valueLabel - current B selection label (for display)
 * @param {Function} onSelect - (combo, well|null) => void
 */
function CompareSelect({ combos = [], valueLabel = '', onSelect }) {
    const { theme } = useTheme()
    const isDark = theme === 'dark'
    const [search, setSearch] = useState('')
    const [open, setOpen] = useState(false)
    const boxRef = useRef(null)

    useEffect(() => {
        const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false) }
        document.addEventListener('mousedown', onDoc)
        return () => document.removeEventListener('mousedown', onDoc)
    }, [])

    const options = useMemo(() => {
        const opts = []
        for (const c of combos) {
            opts.push({ type: 'lease', combo: c.comboCurveTie, well: null, label: c.comboCurveTie })
            for (const w of (c.wells || [])) {
                opts.push({ type: 'well', combo: c.comboCurveTie, well: w.wolfpakWellName, label: `${c.comboCurveTie} › ${w.wolfpakWellName}` })
            }
        }
        return opts
    }, [combos])

    const filtered = useMemo(() => {
        const q = search.toLowerCase().trim()
        if (!q) return options.slice(0, 60)
        return options.filter(o => o.label.toLowerCase().includes(q)).slice(0, 60)
    }, [options, search])

    const pick = (o) => {
        onSelect(o.combo, o.well)
        setSearch('')
        setOpen(false)
    }

    return (
        <div className="relative w-full sm:w-80" ref={boxRef}>
            <input
                type="text"
                value={open ? search : (valueLabel || search)}
                onChange={e => { setSearch(e.target.value); setOpen(true) }}
                onFocus={() => setOpen(true)}
                placeholder={valueLabel ? valueLabel : 'Compare with a lease or well…'}
                className={`w-full px-3 py-2 text-sm rounded-lg border-2 outline-none transition-colors ${
                    isDark
                        ? 'bg-slate-800 border-slate-600 text-gray-100 placeholder-gray-400 focus:border-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                }`}
            />
            {open && filtered.length > 0 && (
                <div className={`absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border-2 shadow-lg ${
                    isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-300'
                }`}>
                    {filtered.map((o, i) => (
                        <button key={`${o.combo}|${o.well || ''}|${i}`}
                            onClick={() => pick(o)}
                            className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                                o.type === 'lease' ? 'font-semibold' : 'pl-6'
                            } ${isDark ? 'text-gray-200 hover:bg-slate-800' : 'text-gray-700 hover:bg-gray-100'}`}
                        >
                            {o.type === 'well' ? o.well : o.label}
                            {o.type === 'lease' && <span className={`ml-2 text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>lease</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}

export default CompareSelect
